import { RefObject, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNotification, useTranslate } from "@refinedev/core";

import { Modal, ModalRef } from "@shared/components";
import { AgreementProductForm } from "../AgreementProductForm";
import { getProductByIdService } from "@modules/defaultProducts";
import { api } from "@api/api";

type EditAgreementProductModalProps = {
  modalRef: RefObject<ModalRef>;
  onComplete?: () => void;
};

export function EditAgreementProductModal({
  modalRef,
  onComplete,
}: EditAgreementProductModalProps) {
  const translate = useTranslate();
  const { open } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const [isOpenMod, setisOpenMod] = useState(false);

  const methods = useForm<any>({
    defaultValues: {
      id: undefined,
      name: "",
      active: true,
      default_product: null,
      metadata: {},
    },
    mode: "onBlur",
  });

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const values = methods.getValues();

      const id = (modalRef.current?.getId?.() as any) || values.id;

      const product = await getProductByIdService(Number(values.default_product));

      let passRequired = true;

      Object.values(product.metadata).forEach((meta) => {
        if (meta.obrigatorio && !values.metadata[meta.key]) {
          passRequired = false;
        }
      });

      if (!passRequired) {
        open?.({ type: "error", message: "Verifique os campos obrigatórios" });
        return;
      }

      const fd = new FormData();
      fd.append("name", String(values.name ?? ""));
      fd.append("active", (!!values.active).toString());
      fd.append("default_product", String(values.default_product));

      const metadataObj = (values.metadata || {}) as Record<string, any>;
      Object.entries(metadataObj).forEach(([key, val]) => {
        const fdKey = `metadata.${key}`;
        if (val instanceof File) {
          fd.append(fdKey, val);
        } else if (
          val &&
          typeof val === "object" &&
          "file" in val &&
          val.file instanceof File
        ) {
          fd.append(fdKey, val.file as File);
        } else if (Array.isArray(val)) {
          fd.append(fdKey, JSON.stringify(val));
        } else {
          fd.append(fdKey, String(val ?? ""));
        }
      });

      await api.patch(`v1/product_admin/${id}/`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      open?.({
        type: "success",
        message: translate("agreement_product_admin.success.update"),
      });
      onComplete?.();
      modalRef.current?.close();
    } catch (error) {
      console.error('Error updating agreement product:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      ref={modalRef}
      title={translate("agreement_product_admin.edit_title")}
      onConfirm={handleConfirm}
      methods={methods}
      isPending={isLoading}
      onClose={() => {
        setisOpenMod(false);
      }}
      onOpen={() => {
        setisOpenMod(true);
      }}
    >
      <FormProvider {...methods}>
        <AgreementProductForm isOpenMod={isOpenMod} />
      </FormProvider>
    </Modal>
  );
}
